import { computed, defineComponent, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import { priceFormatterKey } from "../../../infrastructure/price_formatter";
import Quantity from "../../Shared/Quantity";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

const PRODUCT_ID_MULTISIM = 36;
const MAX_QUANTITY_PER_VARIANT = 2;

const SELECTION_NONE = "none";
const SELECTION_MULTI = "multi";

export default defineComponent({
  template: html,
  components: { Quantity },
  setup() {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);
    const priceFormatter = inject(priceFormatterKey);

    // vars und computed
    const multiSimVariants = computed(() => {
      return orderService?.availableVariants?.value?.filter((v) => v.product_id == PRODUCT_ID_MULTISIM) ?? [];
    });
    const vatType = computed(() => {
      return orderService?.currentVatType.value;
    });

    const quantityFor = (variantId: number) => {
      return orderService?.currentItems?.value?.reduce((sum, it) => {
        if (it.product_variant_id == variantId) {
          sum = sum + it.qty;
        }
        return sum;
      }, 0) ?? 0;
    };

    const totalQuantity = computed(() => {
      return orderService?.currentItems?.value?.reduce((sum, it) => {
        if (it.product_id == PRODUCT_ID_MULTISIM) {
          sum = sum + it.qty;
        }
        return sum;
      }, 0) ?? 0;
    });

    // 'none'/'multi' ist primär lokaler UI-Zustand: solange noch keine Variante
    // gewählt ist, soll die Liste trotzdem sichtbar bleiben, nachdem 'multi'
    // angeklickt wurde. Sobald der Server jedoch eine Menge > 0 meldet, folgt
    // die Auswahl automatisch (z.B. nach Reload).
    const selectionChoice = ref<string>(SELECTION_NONE);
    watch(totalQuantity, (qty) => {
      if (0 < qty) {
        selectionChoice.value = SELECTION_MULTI;
      }
    }, { immediate: true });

    // methods
    const onSelectionChange = async (value: string) => {
      if (SELECTION_NONE == value) {
        const items = orderService?.currentItems?.value?.filter(
          (it) => it.product_id == PRODUCT_ID_MULTISIM && 0 < it.qty,
        ) ?? [];
        // sequentiell, da jeder Request den gemeinsamen Order-State überschreibt
        for (const it of items) {
          await orderService?.removeFromCart(it.product_variant_id, "", it.qty);
        }
      }
      selectionChoice.value = value;
    };

    const onToggleVariant = async (variantId: number, evt: Event) => {
      const checked = (evt.target as HTMLInputElement).checked;
      const current = quantityFor(variantId);
      if (checked && 0 == current) {
        await orderService?.addToCart(variantId, "", 1);
      } else if (!checked && 0 < current) {
        await orderService?.removeFromCart(variantId, "", current);
      }
    };

    // Falls z.B. beide Varianten qty=1 haben und eine auf 2 erhöht wird, reduziert
    // der Server die andere Variante automatisch auf 0. currentItems ist reaktiv,
    // die Quantity-Komponente übernimmt den neuen :value darum automatisch.
    const onQuantityChange = async (variantId: number, newQty: number) => {
      const current = quantityFor(variantId);
      const diff = newQty - current;
      if (0 < diff) {
        await orderService?.addToCart(variantId, "", diff);
      } else if (0 > diff) {
        await orderService?.removeFromCart(variantId, "", -diff);
      }
    };

    // aktuell gibt es keine Pflichtfelder; die Methode existiert, damit ConfigsMobile
    // sie wie die übrigen Kind-Komponenten einheitlich vor 'next' abfragen kann
    const isValid = computed(() => true);

    const validate = async () => {
      return isValid.value;
    };

    return {
      t,
      multiSimVariants,
      vatType,
      priceFormatter,
      quantityFor,
      selectionChoice,
      onSelectionChange,
      onToggleVariant,
      onQuantityChange,
      maxQuantityPerVariant: MAX_QUANTITY_PER_VARIANT,
      isValid,
      validate,
    };
  },
} as any);
